// Buyer's searched location (the consumer's Target Location), single source
// of truth for the priority order used on the browser side:
//   1. Manually chosen in the search/location filter (session/component state).
//   2. The Target Location saved for search (localStorage -- also covers
//      guests). It is an explicit choice, so it wins over any address: someone
//      in Santo Domingo can shop Brooklyn for a relative there.
//   3. Selected shipping/order address for the current session or order.
//   4. Signed-in buyer's default address.
//   5. None -- the server then resolves the destination itself (default
//      address, else approximate IP location), see server_elakay
//      elakay_services/locationContext.js and utils/locationContext.js.
//
// A target may carry a consumer search `radius` (+ `radiusUnit` "mi"|"km"):
// it only NARROWS results to shops near the target; a seller's Strict
// Location zone is always enforced by the server on top of it.
//
// This module owns #2 (persistence) and the final resolveBuyerLocation()
// priority chain; #1/#3/#4 are supplied by the caller since resolving them
// needs data (filter UI state, order, auth) this pure util doesn't have.
// Never falls back to raw device GPS without an explicit user action -- see
// utils/geolocation.js for that, deliberately not auto-invoked here.

const TEMP_LOCATION_STORAGE_KEY = "elakay_buyer_search_location";

// Custom event dispatched by saveTempBuyerLocation() any time the buyer's
// searched location changes, so a component that doesn't own this state
// directly (e.g. Filter_fitlerP.js, which now hosts the location control
// while ProductGridPage.jsx still owns the actual query state) can still be
// notified -- same pattern as CARD_MODE_EVENT in utils/cardDisplaySettings.js.
export const BUYER_LOCATION_EVENT = "elakay-buyer-location-change";

// Dispatched when something the SERVER uses to resolve the destination
// changed without a target change (an address added / edited / made default
// / deleted, sign in / out): utils/locationContext.js refetches the context
// and every location-aware view recomputes (Product Preview shipping, ...).
export const LOCATION_INPUTS_EVENT = "elakay-location-inputs-change";
export const notifyLocationInputsChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LOCATION_INPUTS_EVENT));
};

const RADIUS_UNITS = ["mi", "km"];
const validRadius = (location) => Number(location?.radius) > 0;

export const isValidBuyerLocation = (location) =>
  !!location &&
  typeof location === "object" &&
  Boolean(
    location.city || location.state || location.postalCode || location.country ||
    (Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude)))
  );

export const saveTempBuyerLocation = (location) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (!isValidBuyerLocation(location)) {
    window.localStorage.removeItem(TEMP_LOCATION_STORAGE_KEY);
  } else {
    window.localStorage.setItem(TEMP_LOCATION_STORAGE_KEY, JSON.stringify(location));
  }
  window.dispatchEvent(new CustomEvent(BUYER_LOCATION_EVENT, { detail: isValidBuyerLocation(location) ? location : null }));
};

export const readTempBuyerLocation = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(TEMP_LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidBuyerLocation(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const clearTempBuyerLocation = () => saveTempBuyerLocation(null);

// Maps either address shape used in this app -- User.address[] (city, State,
// postalCode, latitude, longitude) or Order.shippingAddress (city, state,
// postal_code, no coordinates) -- to the flat shape the rest of this module
// and the backend's buyer-location query/body params expect.
export const addressToBuyerLocation = (address) => {
  if (!address) return null;
  const latitude = address.latitude !== undefined && address.latitude !== null ? Number(address.latitude) : null;
  const longitude = address.longitude !== undefined && address.longitude !== null ? Number(address.longitude) : null;
  return {
    country: address.country || "",
    state: address.State || address.state || "",
    city: address.city || "",
    postalCode: address.postalCode || address.postal_code || "",
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
  };
};

// Resolves the location to search with, following mission 2.5's priority
// order. Each argument is optional and already in the flat shape (use
// addressToBuyerLocation() first if starting from a raw address document).
export const resolveBuyerLocation = ({ filterLocation, shippingAddress, userDefaultAddress } = {}) => {
  if (isValidBuyerLocation(filterLocation)) return filterLocation;
  const temp = readTempBuyerLocation();
  if (temp) return temp;
  if (isValidBuyerLocation(shippingAddress)) return shippingAddress;
  if (isValidBuyerLocation(userDefaultAddress)) return userDefaultAddress;
  return null;
};

// Builds the flat param object to merge into a getProductsF/getProductSearch/
// getLakayUser request body (server_elakay reads country/state/city/
// postalCode/latitude/longitude off req.body OR req.query alike -- see
// elakay_services/localSelling.js#resolveBuyerLocationFromRequest). Returns
// null (never an empty object) when there's nothing usable, so callers can
// spread conditionally and omit the fields entirely.
export const buildBuyerLocationParams = (location) => {
  if (!isValidBuyerLocation(location)) return null;
  const params = {};
  if (location.country) params.country = location.country;
  if (location.state) params.state = location.state;
  if (location.city) params.city = location.city;
  if (location.postalCode) params.postalCode = location.postalCode;
  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    params.latitude = latitude;
    params.longitude = longitude;
  }
  if (validRadius(location)) {
    params.radius = Number(location.radius);
    params.radiusUnit = RADIUS_UNITS.includes(location.radiusUnit) ? location.radiusUnit : "mi";
  }
  return params;
};

// Query-param object for an API call: the caller's explicit location, else
// the saved Target Location (so every product/shop request -- Home, search,
// categories, shop pages, Product Preview -- carries the same target without
// each page threading it). `null` = send nothing; the server then falls back
// to the signed-in buyer's address or the approximate IP location.
export const locationQueryParams = (location) => {
  const effective = location === undefined ? readTempBuyerLocation() : location;
  return buildBuyerLocationParams(effective) || {};
};

// Short "near <place>" label for the mission 2.8 indicator text
// ("Produits disponibles près de Brooklyn, NY").
export const describeBuyerLocation = (location) => {
  if (!isValidBuyerLocation(location)) return "";
  let label = "";
  if (location.city && location.state) label = `${location.city}, ${location.state}`;
  else label = location.city || location.state || location.postalCode || location.country || "";
  if (label && location.postalCode && !label.includes(location.postalCode)) label = `${label} ${location.postalCode}`;
  if (validRadius(location)) label = `${label} (+${Number(location.radius)} ${location.radiusUnit === "km" ? "km" : "mi"})`;
  return label;
};
